import React, {useCallback, useEffect, useMemo, useState} from 'react'
import useTranslate from "../../../modules/local/useTranslate";
import {FaRegTrashCan} from "react-icons/fa6";
import {Button} from "antd";
import {FiEdit3} from "react-icons/fi";
import classNames from "classnames";
import {TbEdit, TbTrash} from "react-icons/tb";

// --- CONFIG & UTILITIES ---

/**
 * Utility function to convert a decimal score (e.g., 9.25) back to its base (9) and fraction (0.25).
 * @param {number} decimalScore
 * @returns {{base: number, fraction: number}}
 */
const decimalToParts = (decimalScore) => {
    if (decimalScore === null) return {base: null, fraction: null};
    const base = Math.floor(decimalScore);
    const fraction = decimalScore - base;
    return {base, fraction};
};

const GradeNumberInput = ({defaultValue, onChange, maxScore = 10}) => {
    const t = useTranslate();
    const [isSelectingBase, setIsSelectingBase] = useState(true);
    const [tempBase, setTempBase] = useState(null);

    // Synchronize local state with external prop value
    useEffect(() => {
        if (defaultValue === null) {
            setTempBase(null);
            setIsSelectingBase(true);
        } else {
            const {base, fraction} = decimalToParts(defaultValue);
            setTempBase(base);
            // If a value exists, we skip base selection initially but still show the score.
            // When the user clicks to edit, it will reset to base selection if needed.
        }
    }, [defaultValue]);

    const {base, fraction} = useMemo(() => decimalToParts(defaultValue), [defaultValue]);

    const handleBaseSelect = useCallback((score) => {
        setTempBase(score);
        setIsSelectingBase(false);

        // Điểm tối đa không có phần lẻ → luôn reset về .00 (không giữ lại 0.25/0.5/0.75 của điểm cũ).
        if (score >= maxScore) {
            onChange(maxScore);
            return;
        }

        // If the current score is already set, we immediately calculate the new score
        // by keeping the old fraction. If no score is set, we default to 0.00 fraction.
        const currentFraction = defaultValue !== null ? (defaultValue - Math.floor(defaultValue)) : 0;
        onChange(score + currentFraction);
    }, [onChange, defaultValue, maxScore]);

    const handleFractionSelect = useCallback((frac) => {
        if (tempBase === null) return; // Should not happen

        const newScore = tempBase + frac;
        onChange(newScore);
        setIsSelectingBase(false); // Stay in fraction view until reset/edit
    }, [tempBase, onChange]);

    const handleClear = useCallback(() => {
        onChange(null);
        setTempBase(null);
        setIsSelectingBase(true);
    }, [onChange]);

    const handleEdit = useCallback(() => {
        setIsSelectingBase(true);
    }, []);

    // Scores array: [1, 2, ..., maxScore]
    const scores = useMemo(() => Array.from({length: maxScore}, (_, i) => i + 1), [maxScore]);

    // Fraction display and value mapping
    const fractions = useMemo(() => [
        {display: <span className="flex gap-1 text-lg">{tempBase}</span>, value: 0.00},
        {
            display: <span className="flex gap-1"><span className="font-bold text-xl">{tempBase}</span><span
                className="font-medium text-xs">1/4</span></span>, value: 0.25
        },
        {
            display: <span className="flex gap-1"><span className="font-bold text-xl">{tempBase}</span><span
                className="font-medium text-xs">2/4</span></span>, value: 0.5
        },
        {
            display: <span className="flex gap-1"><span className="font-bold text-xl">{tempBase}</span><span
                className="font-medium text-xs">3/4</span></span>, value: 0.75
        },
    ], [tempBase]);

    const currentFractionDisplay = useMemo(() => {
        const found = fractions.find(f => Math.abs(f.value - fraction) < 0.001);
        return found ? found.display : '—';
    }, [fraction, fractions]);

    const DisplayScore = (
        <div className="flex items-end gap-3 p-4 border border-slate-200 bg-slate-50 shadow-md rounded-2xl relative transition-all duration-300">
            <div className="flex flex-col flex-1">
                <span className="font-medium text-slate-400 tracking-widest uppercase flex-shrink-0">{t('Selected Grade')}</span>
                <span className="font-extrabold text-xl md:text-3xl text-gray-800">
                    {defaultValue === null ? '—' : defaultValue.toFixed(2)}
                </span>
            </div>
            <div className="mb-2 flex space-x-2">
                {defaultValue !== null && (
                    <Button
                        color="default" variant="filled"
                        onClick={handleEdit}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 shadow-md rounded-md no-border transition duration-150"
                        title={t("edit grade")}
                        icon={<TbEdit size={14}/>}
                    />
                )}
                <Button
                    color="primary" variant="filled"
                    onClick={handleClear}
                    className="p-2 bg-primary-50 hover:bg-primary-100 text-primary-600 shadow-md rounded-md no-border transition duration-150"
                    title={t("clear grade")}
                    icon={<TbTrash size={14}/>}
                />
            </div>
        </div>
    );

    const BaseScoreSelector = (
        <div className="flex flex-col gap-1">
            <h3 className="mb-1 font-medium text-slate-500 tracking-wider">
                1. {t('Select Base Grade')}
            </h3>
            <div className="grid grid-cols-5 gap-3">
                {scores.map((s) => {
                    const isSelected = tempBase === s;
                    return (
                        <button
                            key={s}
                            onClick={() => handleBaseSelect(s)}
                            className={classNames(
                                "w-full p-3 flex flex-center font-bold text-base shadow-sm rounded-xl transition-all duration-300",
                                isSelected ?
                                    'border-2 border-secondary-500 bg-secondary-500 text-white' :
                                    'bg-slate-50 text-gray-800 hover:text-secondary-600 border border-slate-100 hover:border-secondary-400 hover:ring-2 hover:ring-indigo-200'
                            )}>
                            {s}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const FractionSelector = (
        <div className="flex flex-col gap-1">
            <h3 className="mb-1 font-medium text-slate-500 tracking-wider">
                2. {t('Select Modifier')}
            </h3>
            <div className="flex justify-between gap-x-3">
                {fractions.map((f) => {
                    const isSelected = Math.abs(f.value - fraction) < 0.001 && tempBase === base;
                    return (
                        <button
                            key={f.value}
                            onClick={() => handleFractionSelect(f.value)}
                            className={classNames(
                                "py-3 flex flex-center flex-1 text-sm font-semibold shadow-sm rounded-xl transition-all duration-300",
                                isSelected ?
                                    'border-2 border-secondary-500 bg-secondary-50 text-secondary-600' :
                                    'bg-slate-50 text-gray-800 hover:text-secondary-600 border border-slate-100 hover:border-secondary-400 hover:ring-2 hover:ring-indigo-200'
                                )}>
                            {f.display}
                        </button>
                    );
                })}
            </div>
            <div
                onClick={handleEdit}
                className="mt-2 text-center font-medium text-indigo-500 hover:text-primary cursor-pointer">
                {t('Edit Base Grade')} ({tempBase})
            </div>
        </div>
    );

    return (
        <div className="w-full flex flex-col gap-6">
            {DisplayScore}
            {isSelectingBase || tempBase === null || tempBase === 10
                ? BaseScoreSelector
                : FractionSelector
            }
        </div>
    );
};

export default GradeNumberInput
